import {Storable} from 'constants/SampleConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {ModelManager} from 'models/managers/ModelManager';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function actions(): any {
  // Inline the import to avoid circular dependency on FinderUtil.
  return require('samples/StorableActions');
}

export default class StorableModelManager extends ModelManager<ManagedModelType.STORABLE> {
  constructor() {
    super(ManagedModelType.STORABLE);
  }

  async doFetchMulti(storableIds: Array<string>): Promise<void> {
    await actions().bulkFetchStorables(storableIds);
    // Deleted storables and storables that we have insufficient privilege to see are simply not included
    const inaccessibleStorableIds = storableIds.filter((id) => !StorableStore.getStorableSummary(id));
    getStore().dispatch(ModelActions.storablesFetchCompleted(inaccessibleStorableIds));
  }

  // For each storable, fetch the full ancestry (unless there's an inaccessible container along the way).
  // Every storable is then stored in the store. Additionally, the map of ancestries is returned by this
  // function.
  //
  // Note that this is not usable with one of DataConnector's methods. You must invoke it manually.
  //
  static async fetchStorablesWithAncestors(
    storableIds: Array<string>
  ): Promise<{[storableId: string]: Array<Storable>}> {
    await actions().bulkGetStorablesWithAncestors(storableIds);
    const inaccessibleStorableIds = storableIds.filter((id) => !StorableStore.getStorable(id));
    getStore().dispatch(ModelActions.storablesFetchCompleted(inaccessibleStorableIds));

    const ancestorsByStorable: {[storableId: string]: Array<Storable>} = {};
    for (const storableId of storableIds) {
      let id: string | undefined | null = storableId;
      const ancestry = [];
      while (id) {
        const storable = StorableStore.getStorable(id);
        // An inaccessible container will still be referenced as the parent id of an accessible storable,
        // even though the storable object is not available in the store
        if (storable == null) {
          break;
        }
        ancestry.push(storable);
        id = storable.parentStorableId;
      }
      ancestorsByStorable[storableId] = ancestry;
    }
    return ancestorsByStorable;
  }

  getModel(modelKey: string): Storable | null {
    return StorableStore.getStorable(modelKey) || null;
  }

  isModelInaccessible(modelKey: string): boolean {
    return getStore().getState().models.inaccessibleStorableIds.includes(modelKey);
  }
}
